/**
 * @warning
 * THIS FILE CONTAINS SHARED LOGIC / EXPECTATIONS WITH AI ENGINE.
 * For logic changes, ensure AI/Amir is looped in to any changes that may impact the AI engine.
 */

import type {
  EmbeddedToolLight,
  ErrorGeneric,
  Memory,
  MemoryConstructionParams,
  MemoryForAI,
  MemoryGroup,
  MemoryGroupForAI,
  MemoryRuleDb,
  MemorySourceEnum,
  MemoryWithHourTimestamps,
  TrackerMemory,
  VariousMemoryIdRefs,
} from '@timelyapp/integration-cloud-api-spec';
import { addMinutes, differenceInSeconds, startOfMinute } from 'date-fns';
import { DateTime } from 'luxon';
import { MEMORY_PARTITIONS_DAYS_IN_FUTURE } from '../constants';
import { getFormattedDomain } from '../memory-parser/tool-matcher';
import { mapSettingsRangeToNumber } from '../utils/memory_config/utils.setting-to-value-map';

/**
 * Checks if two memories should be grouped together.
 * @param group - The memory group to compare with.
 * @param memory - The memory to check.
 * @param params - The parameters to group memories by.
 * @returns True if the memories should be grouped, false otherwise.
 */
export function shouldAddMemoryToGroup(
  group: MemoryGroup,
  memory: Memory,
  params: MemoryConstructionParams
): boolean {
  // TODO: check if this is correct
  if (group.tool?.raw !== memory.tool?.raw) {
    return false;
  }

  // Decide strategy for how to to group Memories
  const toolConfig = getMatchingToolConfig(memory, params);

  const mergeWithinMinutes = mapSettingsRangeToNumber(
    'continuity',
    toolConfig?.preferences?.continuity || 'default',
    params.mergeWithinMinutes
  );

  const groupMemoriesBy =
    toolConfig?.preferences?.group_by === 'default'
      ? params.groupMemoriesBy
      : toolConfig?.preferences?.group_by || params.groupMemoriesBy;

  const parentGroupOn = group[groupMemoriesBy];
  const memoryGroupOn = memory[groupMemoriesBy];
  // Match in group & memory on e.g `memory.tool.id`,`memory.tool.raw` or `memory.context`
  const isObj = typeof parentGroupOn === 'object' && typeof memoryGroupOn === 'object';
  const matchesId = isObj && !!memoryGroupOn?.id && parentGroupOn?.id === memoryGroupOn?.id;
  const matchesRaw = isObj && !!memoryGroupOn?.raw && parentGroupOn?.raw === memoryGroupOn?.raw;
  const matchesValue = !!memoryGroupOn && parentGroupOn === memoryGroupOn;

  const matchesGroupBy = matchesId || matchesRaw || matchesValue;

  const timeGapInMinutes =
    differenceInSeconds(new Date(memory.started_at), new Date(group.ended_at)) / 60;

  // Only group memories if the time gap is less than or equal to mergeWithinMinutes or timeGapInMinutes is negative (indicating overlap)
  return matchesGroupBy && (timeGapInMinutes <= mergeWithinMinutes || timeGapInMinutes <= 0);
}

/**
 * Constructs a MemoryGroup from a memory.
 * @param memory - The memory to construct the group from.
 * @param params - The parameters to order memories by.
 * @returns The constructed MemoryGroup.
 */
export function createNewMemoryGroupFromMemory(
  memory: Memory,
  params: MemoryConstructionParams
): MemoryGroup {
  // Create a new MemoryGroup with only the properties defined in the MemoryGroup type.

  // Decide strategy for how to to group Memories
  const toolConfig = getMatchingToolConfig(memory, params);

  const group_by =
    toolConfig?.preferences?.group_by === 'default'
      ? params.groupMemoriesBy
      : toolConfig?.preferences?.group_by || params.groupMemoriesBy;

  const compact = toolConfig ? toolConfig.preferences?.compact : params.compact;
  const display_title = buildMemoryGroupDisplayName(memory, params);

  const groupContainer = {
    id: memory.id,
    v_id: memory.from_timely ? `group_timely_${memory.id}` : `group_tic_${memory.id}`,
    title: memory.title,
    display_title,
    icon_url: memory.icon_url,
    group_by,
    started_at: memory.started_at,
    ended_at: memory.ended_at,
    color: memory.color || 'rgba(86,210,255,0.30)',
    total_seconds: differenceInSeconds(new Date(memory.ended_at), new Date(memory.started_at)),
    memories: [memory],
    tool: memory.tool || null,
    context: memory.context || null,
    category: memory.category || null,
    signatureKey: `${memory.id}`,
    ignored: memory.ignored || toolConfig?.preferences?.ignored || false,
    starred: isMemoryStarred(memory, params),
    attention_score: 100, // initial memory always has 100% attention, re-calculated in postProcessMemoryGroups
    compact,
    from_timely: !!memory.from_timely,
    tracker_source: memory.tracker_source,
  } satisfies MemoryGroup;

  return groupContainer;
}

/**
 * Mutates a MemoryGroup, adding the Memory and updating the group's ended_at if necessary.
 * @param groupContainer - The MemoryGroup to merge into.
 * @param memory - The memory to merge.
 * @param params - The parameters to order memories by.
 */
export function mutateMemoryGroupWithNewMemory(
  groupContainer: MemoryGroup,
  memory: Memory,
  params: MemoryConstructionParams
): void {
  // If this memory's ended_at is later than the group's, update the group's ended_at.
  if (new Date(memory.ended_at).getTime() > new Date(groupContainer.ended_at).getTime()) {
    groupContainer.ended_at = memory.ended_at;
  }

  // Flag whole MemoryGroup as `ignored` if any sub-memory is ignored
  if (!groupContainer.ignored && memory.ignored) {
    groupContainer.ignored = true;
  }

  // Flag whole MemoryGroup as `starred` if any sub-memory is starred
  if (!groupContainer.starred && isMemoryStarred(memory, params)) {
    groupContainer.starred = true;
  }

  // Mutably add the current memory into the group's memories array.
  if (groupContainer.memories) {
    groupContainer.memories.push(memory);
  } else {
    groupContainer.memories = [memory];
  }
}

/**
 * Sorts memories by their started_at date to ensure we can trust the order of the memories.
 */
export const sortMemoriesByStartTime = (memories: Memory[]): Memory[] => {
  return [...memories].sort(
    (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
  );
};

/** Helper to retrieve any tool config for the current Memory or MemoryGroup */
export const getMatchingToolConfig = (
  memory: Memory | MemoryGroup,
  params: MemoryConstructionParams
) => {
  const toolIdentifier = memory.tool?.id || memory.tool?.raw;
  const toolConfig = toolIdentifier ? params.toolConfigsMap?.[toolIdentifier] : undefined;
  return toolConfig || undefined;
};

/** Helper to check if a memory is starred based on its tool configuration */
export const isMemoryStarred = (
  memory: Memory | MemoryGroup,
  params: MemoryConstructionParams
): boolean => {
  const toolConfig = getMatchingToolConfig(memory, params);
  return !!toolConfig?.preferences?.starred;
};

/**
 * Builds the `display_title` for a MemoryGroup, based on the `namingStrategy` and `groupMemoriesBy` params.
 * @note This used to factor in toolConfig, but we were missing the namingStrategy per config, so removing.
 * @link https://linear.app/timelyapp/issue/TT-1227/tic-timeline-memories-dont-respect-auto-setting-for-titles
 */
const buildMemoryGroupDisplayName = (memory: Memory, params: MemoryConstructionParams) => {
  const toolTitle = memory.tool?.title || memory.tool?.raw;
  const toolId = memory.tool?.id || memory.tool?.raw;
  const toolConfig = !!toolId && params.toolConfigsMap?.[toolId];
  const toolNamingStrategy = toolConfig && toolConfig?.preferences?.naming_strategy;

  const namingStrategy =
    toolNamingStrategy && toolNamingStrategy !== 'default'
      ? toolNamingStrategy
      : params.namingStrategy !== 'auto'
        ? params.namingStrategy
        : params.groupMemoriesBy === 'tool'
          ? 'tool'
          : 'title';

  return (namingStrategy === 'title' ? memory.title : toolTitle) || memory.title;
};

/**
 * Final manipulation of the Memory Groups to calculate total durations, discard unwanted groups, etc.
 */
function postProcessMemoryGroups(
  result: MemoryGroup[],
  params: MemoryConstructionParams
): MemoryGroup[] {
  return result
    .map(groupContainer => {
      // Round start and end times to the full minute to ensure consistency with timestamp system
      // See `timestamps::mutateTimestampRange` for related logic
      const started_at = startOfMinute(new Date(groupContainer.started_at));
      const ended_at_date = new Date(groupContainer.ended_at);
      const ended_at =
        ended_at_date.getSeconds() === 0
          ? ended_at_date
          : addMinutes(startOfMinute(ended_at_date), 1);

      groupContainer.started_at = started_at.toISOString();
      groupContainer.ended_at = ended_at.toISOString();

      // Compute the "seconds of duration" for the group
      // NB: This doesn't account for overlap, and becomes a pretty big problem for Lane calculations. Fix this proper.
      groupContainer.total_seconds = differenceInSeconds(ended_at, started_at);

      // Combine the internal Memories by `title` (aka summarize)
      const memories = groupContainer.memories || [];

      let accurate_total_seconds = 0;
      // Find the Memory with most time spent on it.
      const largestMemory = memories.reduce((acc, memory) => {
        accurate_total_seconds += memory.total_seconds;
        if (memory.total_seconds > acc.total_seconds) return memory;
        return acc;
      }, memories[0]);

      groupContainer.v_id = largestMemory.from_timely
        ? `group_timely_${largestMemory.id}`
        : `group_tic_${largestMemory.id}`;
      // From start->end of group, how much of that time was actually spent in this group?
      const attention_score = Math.round(
        (accurate_total_seconds / groupContainer.total_seconds) * 100
      );

      // TODO:
      // This workaround means we've got double-counting of Memories happening.
      // Look into the root cause of this later.
      groupContainer.attention_score = attention_score > 100 ? 100 : attention_score;

      groupContainer.title = largestMemory.title;
      groupContainer.display_title = buildMemoryGroupDisplayName(largestMemory, params);

      const toolConfig = getMatchingToolConfig(largestMemory, params);

      const toolConfigRelevant =
        !!toolConfig && toolConfig.preferences?.naming_strategy !== 'default';
      const hideTitle = !!toolConfig && toolConfig.preferences?.compact;
      const noNoiseCancelling =
        !!toolConfig && ['off', 'low'].includes(toolConfig.preferences?.noise_cancellation);

      const smartlyCompact =
        hideTitle || (!noNoiseCancelling && groupContainer.total_seconds < 60 * 2);

      groupContainer.compact = !toolConfigRelevant ? groupContainer.compact : smartlyCompact;

      // Sign the group so we have a stable-ish key
      groupContainer.signatureKey = `${groupContainer.memories?.map(m => m.id).join('-')}`;

      return groupContainer;
    })
    .filter(group => {
      const toolConfig = getMatchingToolConfig(group, params);
      const showAllDetails =
        !!toolConfig && ['off'].includes(toolConfig.preferences?.noise_cancellation);

      const minimumGroupDurationMinutes = mapSettingsRangeToNumber(
        'noise_cancellation',
        toolConfig?.preferences?.noise_cancellation || 'default',
        params.minimumGroupDurationMinutes
      );

      const belowMinimumDuration = minimumGroupDurationMinutes > group.total_seconds / 60;

      if (!params.showIgnored && group.ignored) {
        return false;
      }
      // Integration-memories should _always_ show.
      if (!group.tracker_source) {
        return true;
      }
      // Hide memories under minimumGroupDurationMinutes,
      // except if tool is configured to show `full_details`
      if (!showAllDetails && belowMinimumDuration) {
        return false;
      }

      return true;
    });
}

/**
 * Merges overlapping/closely timed Memories from the same `tool.id` into a single MemoryGroup.
 * Respects each toolConfig's `continuity` setting.
 * @param memories - The memories to merge.
 * @param params - The parameters for merging.
 * @returns The merged MemoryGroupContainers.
 */
export function constructMemoryGroups(
  memories: Memory[],
  params: MemoryConstructionParams
): MemoryGroup[] {
  const sortedMemories = sortMemoriesByStartTime(memories);
  const groups: MemoryGroup[] = [];

  sortedMemories.forEach(memory => {
    const toolConfig = getMatchingToolConfig(memory, params);
    const mergeWithinMinutes = mapSettingsRangeToNumber(
      'continuity',
      toolConfig?.preferences?.continuity || 'default',
      params.mergeWithinMinutes
    );

    // Number of records to look back on
    const lookbackLimit = mergeWithinMinutes * 60;

    // Find the index of a recent memory (limited to lookbackLimit for performance) that matches the same tool and is either overlapping or closely timed.
    // This is done by iterating backwards through the result array and finding the index of the first memory that should be grouped with the current memory.
    let matchingGroupIndex = null;

    // Look in most recent to least recent order, for a matching memory to group with.
    for (
      let index = groups.length - 1;
      index >= Math.max(0, groups.length - lookbackLimit);
      index--
    ) {
      if (shouldAddMemoryToGroup(groups[index], memory, params)) {
        matchingGroupIndex = index;
        break;
      }
    }

    // If a group was found, add the current memory to it.
    if (matchingGroupIndex != null) {
      const group = groups[matchingGroupIndex];
      mutateMemoryGroupWithNewMemory(group, memory, params);
    } else {
      // If no parent memory was found, construct a new MemoryGroup from the current memory and add it to the result array.
      groups.push(createNewMemoryGroupFromMemory(memory, params));
    }
  });

  return postProcessMemoryGroups(groups, params);
}

/**
 * Determines the optimal icon_url for a Memory, based on the URL and tool/window_tool.
 * For web-based Memories without a tool, use Timely Icon CDN fallback.
 */
export const constructMemoryIconUrl = ({
  url,
  tool = null,
  windowTool,
  tracker_source = 'mac' as const,
  defaultIconUrl = `${process.env.SITE_URL || ''}/images/tools/default.png`,
}: {
  url?: string | null;
  tool?: Pick<EmbeddedToolLight, 'icon_url' | 'raw' | 'title'> | null;
  windowTool?: Pick<EmbeddedToolLight, 'icon_url'> | null;
  defaultIconUrl?: string;
  tracker_source?: MemorySourceEnum | null;
}): string => {
  const fallbackIconUrl = tool?.icon_url || windowTool?.icon_url || defaultIconUrl;
  const urlEncodedFallbackIconUrl = encodeURIComponent(fallbackIconUrl);

  // If not, try to construct an icon_url from the URL
  let icon_url;

  if (url && url.startsWith('http')) {
    const hostname = getFormattedDomain(url);
    icon_url =
      hostname &&
      `https://icons.timelyapp.com/favicons/${hostname}?size=64&fallback=${urlEncodedFallbackIconUrl}`;
  } else if (tool?.raw || tool?.title) {
    const toolTitle = tool?.raw || tool?.title;
    icon_url = `https://icons.timelyapp.com/apps/${tracker_source}/${toolTitle}?size=64&fallback=${urlEncodedFallbackIconUrl}`;
  }

  // And as final fallback, use specified icon on vercel, or use the window_tool icon_url
  return icon_url || tool?.icon_url || fallbackIconUrl;
};

/**
 * Helper util for converting a Memory to a MemoryForAI
 */
const convertMemoryToAIFormat = (memory: Memory, params: MemoryConstructionParams) => {
  return {
    id: memory.id,
    title: memory.title,
    url: memory.url,
    started_at: memory.started_at,
    ended_at: memory.ended_at,
    context: memory.context,
    category_id: memory.category?.id,
    tool_ref: memory.tool?.id || memory.tool?.raw || undefined,
    window_tool_ref: memory.window_tool?.id || memory.window_tool?.raw || undefined,
    updated_at: memory.updated_at,
    ignored: memory.ignored,
    from_tracker: !!memory.tracker_source,
    tracker_source: memory.tracker_source,
  } satisfies MemoryForAI;
};

/**
 * AI training & inferrence needs less information per-Memory, so we'll construct a simpler Memory for that.
 */
export const constructMemoryForAI = (
  memories: Memory[],
  params: MemoryConstructionParams
): MemoryForAI[] => {
  return memories.map(memory => {
    return convertMemoryToAIFormat(memory, params);
  });
};

/**
 * AI training & inferrence models need less info per-MemoryGroup, so return a simpler group concept for that.
 */
export const constructMemoryGroupForAI = (
  memories: Memory[],
  params: MemoryConstructionParams
): MemoryGroupForAI[] => {
  const groupedMemories = constructMemoryGroups(memories, params);
  return groupedMemories.map(memoryGroup => {
    let updated_at: string | undefined;

    // Format Memories for AI, and keep track of most recently updated_at
    const memories = memoryGroup.memories?.map(memory => {
      if (!updated_at || new Date(memory.updated_at).getTime() > new Date(updated_at).getTime()) {
        updated_at = memory.updated_at;
      }
      return convertMemoryToAIFormat(memory, params);
    });

    return {
      id: memoryGroup.id,
      title: memoryGroup.title,
      started_at: memoryGroup.started_at,
      ended_at: memoryGroup.ended_at,
      category_id: memoryGroup.category?.id,
      tool_ref: memoryGroup.tool?.id || memoryGroup.tool?.raw || undefined,
      updated_at,
      memories,
    } satisfies MemoryGroupForAI;
  });
};

/**
 * Simple checks of validity of Memories.
 * @note hacking on an airplane, probably wanna do this better, either in RPC, or proper validation that explains more the individual errors
 */
export function validateCreateMemoriesPayload(memories: TrackerMemory[]): false | ErrorGeneric {
  if (!Array.isArray(memories)) {
    return {
      code: 'invalid-shape',
      message: 'The payload must be an array of memories',
    };
  }

  const errorousMemories = memories.some(memory => {
    return new Date(memory.started_at) > new Date(memory.ended_at);
  });

  if (errorousMemories) {
    return {
      code: 'invalid-date-range',
      message: 'The start date must be before the end date',
    };
  }
  return false;
}

/**
 * Memory Tracker for Windows sends empty titles on occation, throwing validation errors via ts-rest/Zod.
 * Temporarily discard these here, until Win Tracker itself stops sending invalid memories.
 *
 * Also discard memories that are too far in the future, as we don't have partitions for them.
 */
export function discardInvalidMemories(memories: TrackerMemory[]): TrackerMemory[] {
  return memories.filter(memory => {
    const validTitle = memory.title?.length > 0;

    // The parser calls `new Date(...).toISOString()` on both datetimes; a value
    // that passes Zod's `.datetime({ offset: true })` but still yields an Invalid
    // Date (e.g. an out-of-range UTC offset like `+99:00`) would throw there and
    // reject the whole upload batch (TT-5498).
    const validDates =
      !Number.isNaN(new Date(memory.started_at).getTime()) &&
      !Number.isNaN(new Date(memory.ended_at).getTime());

    // Discard memories that are too far in the future (because we don't have partitions for them)
    const withinPartitionRange =
      DateTime.fromISO(memory.started_at) <
      DateTime.now().plus({ days: MEMORY_PARTITIONS_DAYS_IN_FUTURE - 1 }); // Edge is a bit iffy with timezones and all, so we'll go one day less

    return validTitle && validDates && withinPartitionRange;
  });
}

/**
 * Some memories contain strings that are valid UTF-8.
 * They are valid in JSON, but not valid to PostgreSQL.
 * We just remove them for now.
 */
export function sanitizeMemories(memories: TrackerMemory[]): TrackerMemory[] {
  // Only remove NULL character and other problematic control chars
  // Preserve: \t (tab, \u0009), \n (newline, \u000A), \r (carriage return, \u000D)
  const sanitizeString = (str: string) =>
    // biome-ignore lint/suspicious/noControlCharactersInRegex: intentionally stripping dangerous control chars
    str.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

  return memories.map(memory => {
    memory.title = sanitizeString(memory.title);
    if (memory.app) {
      memory.app = sanitizeString(memory.app);
    }
    if (memory.context) {
      memory.context = sanitizeString(memory.context);
    }
    if (memory.url) {
      memory.url = sanitizeString(memory.url);
    }
    return memory;
  });
}

/**
 * Applies the given rules to the given memory.
 * @param rules - The rules to apply.
 * @param memory - The TrackerMemoryParsed to apply the rules to.
 * @note Only `ignore` rules are supported for now.
 */
export const applyRulesToMemory = (rules: MemoryRuleDb[], memory: TrackerMemory): TrackerMemory => {
  // Map from MemoryRuleDb fields to TrackerMemoryParsed fields
  const fieldMap: {
    [key in MemoryRuleDb['field']]: keyof Pick<TrackerMemory, 'title' | 'url' | 'app'>;
  } = {
    title: 'title',
    url: 'url',
    tool: 'app',
  };

  // Loop over the rules...
  rules.forEach(rule => {
    const memoryField = fieldMap[rule.field];
    const memoryFieldValue = memory[memoryField];

    if (!memoryFieldValue) return;

    const fieldValue = memoryFieldValue.toLowerCase();
    const ruleValue = rule.value.toLowerCase();
    let ruleApplies = false;

    // And if the rule applies...
    switch (rule.matcher) {
      case 'equals':
        ruleApplies = fieldValue === ruleValue;
        break;
      case 'contains':
        ruleApplies = fieldValue.includes(ruleValue);
        break;
      case 'startsWith':
        ruleApplies = fieldValue.startsWith(ruleValue);
        break;
      case 'endsWith':
        ruleApplies = fieldValue.endsWith(ruleValue);
        break;
    }

    // Ignore or rewrite the memory
    if (ruleApplies) {
      switch (rule.type) {
        case 'ignore':
          memory.ignored = true;
          break;
        case 'rewrite': {
          // Replace the memory[key] with the rewrite_to[key] value
          const rewriteToField = rule.rewrite_to?.[rule.field] || null;
          if (rewriteToField) {
            memory[memoryField] = rewriteToField;
          }
          break;
        }
        default:
          break;
      }
    }
  });

  return memory;
};

/**
 * Compacts memories within a MemoryGroup by consolidating them on `title` (by default)
 *
 * @param memoryGroup - The MemoryGroup containing the memories to be compacted.
 * @param opts.groupBy - Configuration the property to group by (only `title` atm)
 * @returns An array of Memory objects after compacting.
 */

export const compactMemoriesByProperty = (
  memories: Memory[] = [],
  {
    groupBy,
    embedTimestamps,
  }: { groupBy: keyof Memory | ((memory: Memory) => string); embedTimestamps?: boolean } = {
    groupBy: 'title',
    embedTimestamps: false,
  }
): MemoryWithHourTimestamps[] | Memory[] => {
  const memoryMap = memories.reduce(
    (acc, memory) => {
      let key: string;
      if (typeof groupBy === 'function') {
        key = groupBy(memory);
      } else {
        key = String(memory[groupBy]);
      }
      const existingMemory = acc[key];
      // Extend the existing memory with updated total_seconds and ended_at
      // NB: This is a terrible way to do it, as it doesn't account for gaps/overlap in a sane way.
      //     But it's what we have for now, and it's better than nothing.

      const timestamp = embedTimestamps && {
        from: memory.started_at,
        to: memory.ended_at,
        entry_ids: [`${memory.id}` as VariousMemoryIdRefs],
      };

      if (existingMemory) {
        existingMemory.total_seconds += memory.total_seconds;
        if (new Date(memory.ended_at) > new Date(existingMemory.ended_at)) {
          existingMemory.ended_at = memory.ended_at;
        }

        if (embedTimestamps && 'timestamps' in existingMemory && !!timestamp) {
          existingMemory.timestamps?.push(timestamp);
        }
      } else {
        if (embedTimestamps && timestamp) {
          acc[key] = { ...memory, timestamps: [timestamp] };
        } else {
          acc[key] = { ...memory };
        }
      }
      return acc;
    },
    {} as Record<string, Memory | MemoryWithHourTimestamps>
  );

  return Object.values(memoryMap).sort((a, b) => b.total_seconds - a.total_seconds);
};
